import { useEffect, useRef, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HERO_IMG =
  "https://readdy.ai/api/search-image?query=Vast%20bright%20alpine%20mountain%20landscape%20at%20clear%20morning%20light%2C%20monumental%20snow%20covered%20peaks%20under%20soft%20pale%20blue%20sky%20with%20drifting%20mist%2C%20a%20small%20contemporary%20timber%20and%20glass%20retreat%20nested%20low%20in%20the%20valley%20among%20dark%20pine%20forest%2C%20architecture%20intentionally%20small%20compared%20with%20the%20mountain%2C%20cool%20mineral%20palette%20of%20snow%20white%20mist%20grey%20and%20muted%20pine%20green%2C%20fine%20art%20high%20altitude%20editorial%20landscape%20photography%2C%20quiet%20luxury%2C%20natural%20light%2C%20clean%20composition%20with%20generous%20negative%20space&width=2400&height=1400&seq=alpine-hero-daylight-01&orientation=landscape&nocache=true";

const HERO_VIDEO =
  "https://storage.helloreaddy.io/project_files/be20c2ad-24ea-4944-a38f-0095df10f5d8/ec7e23fe-ec9b-4c41-a5d2-abd804804152_Mountain_hotel_background_video__20260911175328.mp4";

export default function Hero() {
  const rootRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoVisible, setVideoVisible] = useState(false);
  const hasStartedRef = useRef(false);
  const FADE_OUT_MS = 500;

  useEffect(() => {
    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReduced) return;
    const video = videoRef.current;
    if (!video) return;

    const onCanPlay = () => {
      if (hasStartedRef.current) return;
      hasStartedRef.current = true;
      video.play().then(() => setVideoVisible(true)).catch(() => {});
    };
    video.addEventListener("canplay", onCanPlay, { once: true });

    const onTimeUpdate = () => {
      const dur = video.duration;
      if (!isFinite(dur) || dur <= 0) return;
      const remaining = dur - video.currentTime;
      if (remaining <= FADE_OUT_MS / 1000 + 0.05) {
        setVideoVisible(false);
      }
    };
    video.addEventListener("timeupdate", onTimeUpdate);

    const onEnded = () => {
      setVideoVisible(false);
      try {
        video.pause();
      } catch {
        /* noop */
      }
    };
    video.addEventListener("ended", onEnded);

    return () => {
      video.removeEventListener("canplay", onCanPlay);
      video.removeEventListener("timeupdate", onTimeUpdate);
      video.removeEventListener("ended", onEnded);
    };
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Subtle parallax on background image within the sticky container
      gsap.to(".hero-bg", {
        yPercent: 8,
        ease: "none",
        scrollTrigger: {
          trigger: rootRef.current,
          start: "top top",
          end: "bottom top",
          scrub: true,
        },
      });

      // Stage 1 UI fades out
      gsap.to(".hero-stage1", {
        opacity: 0,
        y: -20,
        ease: "none",
        scrollTrigger: {
          trigger: rootRef.current,
          start: "top top",
          end: "20% top",
          scrub: true,
        },
      });

      // Overlay (mist) fades in — the base that gets "cut through" by the wordmark
      gsap.to(".hero-overlay", {
        opacity: 1,
        ease: "none",
        scrollTrigger: {
          trigger: rootRef.current,
          start: "10% top",
          end: "55% top",
          scrub: true,
        },
      });

      // Wordmark reveal — grows in as overlay covers the scene
      gsap.fromTo(
        ".hero-word",
        { opacity: 0, scale: 0.94, letterSpacing: "0.06em" },
        {
          opacity: 1,
          scale: 1,
          letterSpacing: "-0.03em",
          ease: "none",
          scrollTrigger: {
            trigger: rootRef.current,
            start: "18% top",
            end: "55% top",
            scrub: true,
          },
        }
      );

      // Fine caption under the wordmark, appears late
      gsap.fromTo(
        ".hero-caption",
        { opacity: 0, y: 12 },
        {
          opacity: 1,
          y: 0,
          ease: "none",
          scrollTrigger: {
            trigger: rootRef.current,
            start: "50% top",
            end: "70% top",
            scrub: true,
          },
        }
      );

      // Hand-off — everything softly fades before next section
      gsap.to(".hero-pin", {
        opacity: 0.6,
        ease: "none",
        scrollTrigger: {
          trigger: rootRef.current,
          start: "80% top",
          end: "bottom top",
          scrub: true,
        },
      });
    }, rootRef);
    return () => ctx.revert();
  }, []);

  return (
    <section
      id="top"
      ref={rootRef}
      className="relative w-full bg-background-100"
      style={{ height: "260vh" }}
    >
      <div className="hero-pin sticky top-0 h-screen min-h-[720px] w-full overflow-hidden">
        {/* Landscape */}
        <div className="hero-bg absolute inset-0 will-change-transform">
          <img
            src={HERO_IMG}
            alt="Alpine landscape at morning light"
            className="absolute inset-0 w-full h-full object-cover object-center"
          />
          <video
            ref={videoRef}
            src={HERO_VIDEO}
            muted
            playsInline
            preload="auto"
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover object-center transition-opacity ease-out"
            style={{
              opacity: videoVisible ? 1 : 0,
              transitionDuration: videoVisible ? "1400ms" : `${FADE_OUT_MS}ms`,
            }}
          />
          {/* very light top gradient just for nav legibility */}
          <div className="absolute inset-x-0 top-0 h-32 bg-gradient-to-b from-black/25 to-transparent pointer-events-none" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
        </div>

        {/* Mist overlay — fades in on scroll, becomes the surface the wordmark cuts through */}
        <div
          className="hero-overlay absolute inset-0 bg-background-50 pointer-events-none"
          style={{ opacity: 0 }}
        />

        {/* Masked wordmark — background-clip:text reveals the same landscape through the letters */}
        <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none px-6">
          <h1
            className="hero-word font-display font-light text-center leading-[0.86] tracking-tight text-transparent select-none"
            style={{
              backgroundImage: `url(${HERO_IMG})`,
              backgroundSize: "120% auto",
              backgroundPosition: "center 40%",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              fontSize: "clamp(64px, 19vw, 340px)",
              opacity: 0,
            }}
          >
            <span className="block">ALPINE</span>
            <span className="block -mt-[0.08em]">RETREAT</span>
          </h1>
          <div className="hero-caption mt-8 md:mt-10 flex items-center gap-4 text-foreground-700" style={{ opacity: 0 }}>
            <span className="w-8 h-px bg-foreground-700/50" />
            <span className="eyebrow">Above the ordinary — Est. MMXXV</span>
            <span className="w-8 h-px bg-foreground-700/50" />
          </div>
        </div>

        {/* Stage 1 — minimal ambient UI over the landscape */}
        <div className="hero-stage1 absolute inset-x-0 bottom-0 z-10 px-6 md:px-10 pb-8 md:pb-10">
          <div className="flex items-end justify-between gap-8 text-background-50">
            <div className="max-w-md">
              <h2 className="font-display font-light text-background-50 leading-[0.98] tracking-[-0.01em] text-[28px] md:text-[36px]">
                Alpine Retreat
              </h2>
              <p className="mt-4 font-light text-background-50/90 text-sm md:text-[15px] leading-relaxed max-w-sm">
                A quiet mountain hideaway above the clouds — where stillness, light, and alpine air shape every stay.
              </p>
              <div className="mt-5 flex items-center gap-2 text-background-50/70">
                <span className="w-6 h-px bg-background-50/40" />
                <span className="eyebrow text-[10px]">The Alps — 2,148 m</span>
              </div>
            </div>
            <div className="flex items-center gap-3 text-background-50/85 pb-1">
              <span className="eyebrow hidden sm:inline">Scroll to enter</span>
              <span className="w-6 h-6 flex items-center justify-center">
                <i className="ri-arrow-down-line text-sm"></i>
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}